'use client';

import { ZERO } from '@morpho-org/dapp-core';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { formatUnits, parseUnits } from 'viem';
interface UseAssetInputProps {
  value: bigint | null;
  decimals: number;
  debouncedTime?: number;
  onChange: (value: bigint) => void;
}
const useAssetInput = ({
  value,
  decimals,
  debouncedTime = 100,
  onChange
}: UseAssetInputProps) => {
  const bigintValue = useRef<bigint | undefined>(undefined);
  const [valueStr, setValueStr] = useState<string>('');
  const [isPending, setIsPending] = useState(false);
  const debouncedOnChange = useDebouncedCallback((value_0: bigint) => {
    onChange?.(value_0);
    setIsPending(false);
  }, debouncedTime);
  useEffect(() => {
    if (!value) {
      if (bigintValue.current && bigintValue.current > 0n) setValueStr('');
      bigintValue.current = ZERO;
      return;
    }
    try {
      const formatted = formatUnits(value, decimals);
      if (bigintValue.current !== value) {
        setValueStr(formatted);
        bigintValue.current = value;
      }
    } catch {
      // ignore formatting errors and keep the previous displayed value
    }
  }, [value, decimals]);

  /**
   * @param {string} value - The value of the input field. This function is called when the input field value changes. It will parse the value making sure that l locals are correctly handled and call the onChange callback with the parsed value.
   * @returns {void}
   */
  const handleChange = useCallback((value_1: string) => {
    try {
      if (value_1 === '') {
        bigintValue.current = ZERO;
        setValueStr(value_1);
        debouncedOnChange.cancel();
        onChange(ZERO);
        setIsPending(false);
        return;
      }
      const _value = value_1.replaceAll(',', '.');
      const pattern = `^\\d*(\\.)?(\\d+)?$`;
      const isValidRegex = new RegExp(pattern);
      const isValid = isValidRegex.test(_value);
      if (!isValid) return;

      // Limit the number of decimals to the max allowed eg USDC has 6 decimals
      const endsWithDot = _value.endsWith('.');
      const [integer, decsInitial] = _value.split('.');
      let decs = decsInitial;
      if (decs && decs.length > decimals) decs = decs.slice(0, decimals);
      let slicedValue = decs ? [integer, decs].join('.') : integer!;
      if (!integer) slicedValue = '0' + slicedValue;
      bigintValue.current = parseUnits(slicedValue, decimals);
      if (endsWithDot && !decs) slicedValue += '.';
      setValueStr(slicedValue);
      if (bigintValue.current !== undefined) {
        setIsPending(true);
        debouncedOnChange(bigintValue.current);
      }
    } catch {
      // ignore parse errors from partial/invalid numeric input
    }
  }, [debouncedOnChange, onChange, decimals]);
  const flush = useCallback(() => {
    debouncedOnChange.flush();
  }, [debouncedOnChange]);
  return {
    valueStr,
    handleChange,
    isPending,
    flush
  };
};
export default useAssetInput;